/**
 * Receipt OCR Helper Utilities
 * Supports Thai & English OCR recognition, image preprocessing, intelligent parsing,
 * and text file export for Dental Fang Hospital CSSD.
 */

const TESSERACT_CDN_URL = 'https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/tesseract.min.js';

/**
 * Dynamically loads Tesseract.js from CDN if not already loaded in the window.
 * @returns {Promise<any>} Resolves to window.Tesseract
 */
export const loadTesseract = () => {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      return reject(new Error('Tesseract can only be loaded in a browser environment'));
    }

    if (window.Tesseract) {
      return resolve(window.Tesseract);
    }

    // Check if script element already exists
    const existingScript = document.querySelector(`script[src="${TESSERACT_CDN_URL}"]`);
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.Tesseract));
      existingScript.addEventListener('error', (err) => reject(err));
      return;
    }

    const script = document.createElement('script');
    script.src = TESSERACT_CDN_URL;
    script.async = true;
    script.onload = () => {
      if (window.Tesseract) {
        resolve(window.Tesseract);
      } else {
        reject(new Error('Tesseract loaded but window.Tesseract is undefined'));
      }
    };
    script.onerror = (err) => {
      reject(new Error('Failed to load Tesseract.js from CDN: ' + (err?.message || 'Network error')));
    };
    document.head.appendChild(script);
  });
};

/**
 * Preprocesses receipt image to improve OCR accuracy.
 * Converts image to high-contrast grayscale using luminosity and contrast stretch.
 * Gracefully falls back to original imageSource if Canvas is unsupported.
 * 
 * @param {string|Blob|File|HTMLImageElement|HTMLCanvasElement} imageSource
 * @returns {Promise<string|any>} Data URL or original source
 */
export const preprocessReceiptImage = async (imageSource) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return imageSource;
  }

  try {
    const canvas = document.createElement('canvas');
    if (!canvas || !canvas.getContext) {
      return imageSource;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return imageSource;
    }

    // Load image into HTMLImageElement if string or Blob
    let imgElement;
    let tempObjectUrl = null;

    if (typeof imageSource === 'string') {
      imgElement = new Image();
      imgElement.crossOrigin = 'Anonymous';
      imgElement.src = imageSource;
      await new Promise((res, rej) => {
        imgElement.onload = res;
        imgElement.onerror = rej;
      });
    } else if (imageSource instanceof Blob || (typeof File !== 'undefined' && imageSource instanceof File)) {
      tempObjectUrl = URL.createObjectURL(imageSource);
      imgElement = new Image();
      imgElement.src = tempObjectUrl;
      await new Promise((res, rej) => {
        imgElement.onload = res;
        imgElement.onerror = rej;
      });
    } else if (imageSource && imageSource.tagName === 'IMG') {
      imgElement = imageSource;
    } else if (imageSource && imageSource.tagName === 'CANVAS') {
      return imageSource.toDataURL ? imageSource.toDataURL('image/png') : imageSource;
    } else {
      return imageSource;
    }

    const width = imgElement.naturalWidth || imgElement.width || 1200;
    const height = imgElement.naturalHeight || imgElement.height || 1600;

    canvas.width = width;
    canvas.height = height;

    // Draw original image
    ctx.drawImage(imgElement, 0, 0, width, height);

    if (tempObjectUrl) {
      URL.revokeObjectURL(tempObjectUrl);
    }

    // Get pixel data for contrast & grayscale transformation
    const imgData = ctx.getImageData(0, 0, width, height);
    const data = imgData.data;

    // Contrast boost factor (contrast level: 35)
    const contrast = 35;
    const factor = (259 * (contrast + 255)) / (255 * (259 - contrast));

    for (let i = 0; i < data.length; i += 4) {
      // Standard Luminosity Formula for Grayscale
      const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      // Boost contrast
      const contrasted = factor * (gray - 128) + 128;
      const finalVal = Math.max(0, Math.min(255, contrasted));

      data[i] = finalVal;
      data[i + 1] = finalVal;
      data[i + 2] = finalVal;
    }

    ctx.putImageData(imgData, 0, 0);
    return canvas.toDataURL('image/png');
  } catch (err) {
    // If CORS or canvas manipulation fails, fallback gracefully to original
    console.warn('Receipt image preprocessing fallback to raw source:', err);
    return imageSource;
  }
};

/**
 * Performs OCR on receipt image using Thai & English recognition models.
 * 
 * @param {string|Blob|File} imageSource
 * @param {Function} onProgress Callback receiving ({ status: string, progress: number })
 * @returns {Promise<{ text: string, confidence: number }>}
 */
export const performReceiptOCR = async (imageSource, onProgress = () => {}) => {
  try {
    onProgress({ status: 'กำลังเตรียมและปรับความคมชัดของภาพ...', progress: 10 });
    const preprocessed = await preprocessReceiptImage(imageSource);

    onProgress({ status: 'กำลังเตรียมระบบแปลงข้อความ (Tesseract)...', progress: 25 });
    const Tesseract = await loadTesseract();

    onProgress({ status: 'กำลังอ่านตัวอักษรไทย-อังกฤษ...', progress: 40 });

    const result = await Tesseract.recognize(
      preprocessed,
      'tha+eng',
      {
        logger: (m) => {
          if (m && m.status === 'recognizing text') {
            const pct = Math.min(95, Math.round(40 + (m.progress || 0) * 55));
            onProgress({ status: 'กำลังอ่านตัวอักษรไทย-อังกฤษ...', progress: pct });
          }
        }
      }
    );

    onProgress({ status: 'วิเคราะห์ข้อมูลสำเร็จ', progress: 100 });

    return {
      text: result?.data?.text || '',
      confidence: result?.data?.confidence || 0
    };
  } catch (error) {
    console.error('Receipt OCR failed:', error);
    throw error;
  }
};

// Thai Month Name Dictionary
const THAI_MONTHS_MAP = {
  'ม.ค.': '01', 'มกราคม': '01',
  'ก.พ.': '02', 'กุมภาพันธ์': '02',
  'มี.ค.': '03', 'มีนาคม': '03',
  'เม.ย.': '04', 'เมษายน': '04',
  'พ.ค.': '05', 'พฤษภาคม': '05',
  'มิ.ย.': '06', 'มิถุนายน': '06',
  'ก.ค.': '07', 'กรกฎาคม': '07',
  'ส.ค.': '08', 'สิงหาคม': '08',
  'ก.ย.': '09', 'กันยายน': '09',
  'ต.ค.': '10', 'ตุลาคม': '10',
  'พ.ย.': '11', 'พฤศจิกายน': '11',
  'ธ.ค.': '12', 'ธันวาคม': '12'
};

const ENG_MONTHS_MAP = {
  'jan': '01', 'january': '01',
  'feb': '02', 'february': '02',
  'mar': '03', 'march': '03',
  'apr': '04', 'april': '04',
  'may': '05',
  'jun': '06', 'june': '06',
  'jul': '07', 'july': '07',
  'aug': '08', 'august': '08',
  'sep': '09', 'september': '09',
  'oct': '10', 'october': '10',
  'nov': '11', 'november': '11',
  'dec': '12', 'december': '12'
};

/**
 * Normalizes year: converts Buddhist Era (BE) to Common Era (CE).
 * E.g. 2568 -> 2025, 68 -> 2025.
 */
const normalizeYear = (yrStr) => {
  let yr = parseInt(yrStr, 10);
  if (isNaN(yr)) return new Date().getFullYear();

  // 2-digit year
  if (yr < 100) {
    if (yr >= 50) {
      // e.g. 68 -> 2568 BE -> 2025 CE
      yr = (2500 + yr) - 543;
    } else {
      // e.g. 25 -> 2025 CE
      yr = 2000 + yr;
    }
  } else if (yr >= 2400) {
    // 4-digit BE
    yr = yr - 543;
  }
  return yr;
};

/**
 * Intelligently parses raw receipt OCR text into structured fields.
 * 
 * @param {string} rawText Raw text string from OCR
 * @param {string[]} supplierSuggestions List of known supplier company names
 * @returns {object} Extracted structured data
 */
export const parseReceiptText = (rawText = '', supplierSuggestions = []) => {
  const text = typeof rawText === 'string' ? rawText : '';
  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);

  let receiptNumber = '';
  let poNumber = '';
  let supplierName = '';
  let receivedDate = '';
  let totalAmount = '';
  const detectedItems = [];

  // ----------------------------------------------------
  // 1. Receipt / Invoice Number Extraction
  // ----------------------------------------------------
  const receiptPatterns = [
    /(?:ใบเสร็จรับเงินเลขที่|เลขที่ใบเสร็จ|ใบกำกับภาษีเลขที่|บิลเลขที่|เลขที่\s*(?:เอกสาร|ใบเสร็จ|บิล)?|เลขที่)\s*[:#.]?\s*([A-Za-z0-9/-]+)/i,
    /(?:TAX\s*INVOICE|TAX\s*INV|INVOICE|INV|RECEIPT|DOC)(?:\s*NO\.?)?\s*[:#.]?\s*([A-Za-z0-9/-]+)/i,
    /(?:No\.)\s*[:#.]?\s*([A-Za-z0-9/-]+)/i,
    /\b(?:INV|IV)\s*[:#.]\s*([A-Za-z0-9/-]+)/i
  ];

  for (const pat of receiptPatterns) {
    const match = text.match(pat);
    if (match && match[1]) {
      const candidate = match[1].replace(/^[.:\s]+|[.:\s]+$/g, '');
      // Avoid false positive if it's just a common word or very short
      if (candidate.length >= 2 && !['PO', 'DATE', 'TEL', 'FAX', 'NO'].includes(candidate.toUpperCase())) {
        receiptNumber = candidate;
        break;
      }
    }
  }

  // ----------------------------------------------------
  // 2. PO Number Extraction
  // ----------------------------------------------------
  const poPatterns = [
    /(?:ใบสั่งซื้อ\s*(?:เลขที่)?|Purchase\s*Order\s*(?:No\.?)?)\s*[:#.]?\s*([A-Za-z0-9/-]+)/i,
    /P\/O\s*(?:No\.?)?\s*[:#.]?\s*([A-Za-z0-9/-]+)/i,
    /\bPO\s*[:#.]?\s*([A-Za-z0-9/-]+)/i
  ];

  for (const pat of poPatterns) {
    const match = text.match(pat);
    if (match && match[1]) {
      const candidate = match[1].replace(/^[.:\s]+|[.:\s]+$/g, '');
      if (candidate.length >= 2 && candidate.toUpperCase() !== receiptNumber.toUpperCase()) {
        poNumber = candidate;
        break;
      }
    }
  }

  // ----------------------------------------------------
  // 3. Supplier Name Extraction
  // ----------------------------------------------------
  // First priority: Cross-check with supplierSuggestions
  if (Array.isArray(supplierSuggestions) && supplierSuggestions.length > 0) {
    let bestMatch = '';
    for (const sup of supplierSuggestions) {
      if (!sup || !sup.trim()) continue;
      const cleanSup = sup.trim();
      if (text.toLowerCase().includes(cleanSup.toLowerCase())) {
        if (cleanSup.length > bestMatch.length) {
          bestMatch = cleanSup;
        }
      }
    }
    if (bestMatch) {
      supplierName = bestMatch;
    }
  }

  // Second priority: Look for Thai company prefixes or indicators in lines
  if (!supplierName) {
    const companyKeywordsRegex = /(?:บริษัท|หจก\.|บจก\.|บมจ\.)\s*([^\n\r,]+)/i;
    const match = text.match(companyKeywordsRegex);
    if (match && match[0]) {
      let candidate = match[0].replace(/^(?:ชื่อผู้ขาย|ผู้จำหน่าย|จาก|Supplier|Vendor)\s*[:#.]?\s*/i, '').trim();
      // Remove trailing phone numbers or tax ids if appended
      candidate = candidate.split(/(?:โทร|Tel|TAX|สาขา|เลขประจำตัว)/i)[0].trim();
      supplierName = candidate;
    }
  }

  // Third priority: English / Medical company indicators
  if (!supplierName) {
    for (const line of lines.slice(0, 10)) { // usually in the header lines
      if (/(?:Dental|Supply|Co\.,?\s*Ltd|Corporation|Pharmacy|Medical|Lab\b)/i.test(line)) {
        let candidate = line.replace(/^(?:Supplier|Vendor|Sold By|From)\s*[:#.]?\s*/i, '').trim();
        candidate = candidate.split(/(?:Tel|Phone|Tax ID)/i)[0].trim();
        if (candidate.length > 3) {
          supplierName = candidate;
          break;
        }
      }
    }
  }

  // ----------------------------------------------------
  // 4. Received Date Extraction
  // ----------------------------------------------------
  // A. Thai named months: e.g. 15 ม.ค. 2568 or 15 มกราคม 2568
  const thaiMonthNames = Object.keys(THAI_MONTHS_MAP).join('|').replace(/\./g, '\\.');
  const thaiDateRegex = new RegExp(`(\\d{1,2})\\s*(${thaiMonthNames})\\s*(\\d{2,4})`, 'i');
  const thaiDateMatch = text.match(thaiDateRegex);

  if (thaiDateMatch) {
    const day = String(parseInt(thaiDateMatch[1], 10)).padStart(2, '0');
    const monthKey = thaiDateMatch[2];
    const month = THAI_MONTHS_MAP[monthKey] || '01';
    const year = normalizeYear(thaiDateMatch[3]);
    receivedDate = `${year}-${month}-${day}`;
  }

  // B. English named months: e.g. 15 Jan 2025 or 15 January 2025
  if (!receivedDate) {
    const engDateRegex = /(\d{1,2})\s*(Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\s*(\d{2,4})/i;
    const engDateMatch = text.match(engDateRegex);
    if (engDateMatch) {
      const day = String(parseInt(engDateMatch[1], 10)).padStart(2, '0');
      const mName = engDateMatch[2].toLowerCase().substring(0, 3);
      const month = ENG_MONTHS_MAP[mName] || '01';
      const year = normalizeYear(engDateMatch[3]);
      receivedDate = `${year}-${month}-${day}`;
    }
  }

  // C. Numeric dates: DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY
  if (!receivedDate) {
    const numDateRegex = /(?:วันที่|Date)?\s*[:#.]?\s*(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})/i;
    const numDateMatch = text.match(numDateRegex);
    if (numDateMatch) {
      const day = String(parseInt(numDateMatch[1], 10)).padStart(2, '0');
      const month = String(parseInt(numDateMatch[2], 10)).padStart(2, '0');
      const year = normalizeYear(numDateMatch[3]);
      // Basic sanity check on day & month
      if (parseInt(month, 10) >= 1 && parseInt(month, 10) <= 12 && parseInt(day, 10) >= 1 && parseInt(day, 10) <= 31) {
        receivedDate = `${year}-${month}-${day}`;
      }
    }
  }

  // D. ISO format: YYYY-MM-DD
  if (!receivedDate) {
    const isoDateMatch = text.match(/(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})/);
    if (isoDateMatch) {
      const year = normalizeYear(isoDateMatch[1]);
      const month = String(parseInt(isoDateMatch[2], 10)).padStart(2, '0');
      const day = String(parseInt(isoDateMatch[3], 10)).padStart(2, '0');
      if (parseInt(month, 10) >= 1 && parseInt(month, 10) <= 12 && parseInt(day, 10) >= 1 && parseInt(day, 10) <= 31) {
        receivedDate = `${year}-${month}-${day}`;
      }
    }
  }

  // ----------------------------------------------------
  // 5. Total Amount Extraction
  // ----------------------------------------------------
  const totalKeywords = [
    /(?:รวมทั้งสิ้น|จำนวนเงินรวม|ยอดรวมทั้งสิ้น|ยอดรวมสุทธิ|ยอดสุทธิ|สุทธิ|มูลค่ารวม)\s*[:#]?\s*(?:฿|THB)?\s*((?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d{1,2})?)/i,
    /(?:Grand\s*Total|Total\s*Amount|Net\s*Total|Total|Amount\s*Due)\s*[:#]?\s*(?:฿|THB)?\s*((?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d{1,2})?)/i
  ];

  for (const pat of totalKeywords) {
    const match = text.match(pat);
    if (match && match[1]) {
      const cleanNum = match[1].replace(/,/g, '');
      const parsedVal = parseFloat(cleanNum);
      if (!isNaN(parsedVal) && parsedVal > 0) {
        totalAmount = parsedVal.toFixed(2);
        break;
      }
    }
  }

  // ----------------------------------------------------
  // 6. Detected Line Items (Heuristic Extraction)
  // ----------------------------------------------------
  for (const line of lines) {
    // Match line pattern: [Item Name] [Quantity] [Optional Unit] [Optional Price]
    const itemMatch = line.match(/^(\d+[.)]\s*)?([A-Za-zก-๙0-9\s()./+-]+?)\s+(\d+(?:\.\d+)?)\s*(กล่อง|ชิ้น|อัน|แพ็ค|ขวด|หลอด|ม้วน|ชุด|แผง|ถุง|ซอง|กระป๋อง|box|pcs|pack|set|bottle)?\s*(?:@\s*)?([0-9]{1,3}(?:,[0-9]{3})*(?:\.[0-9]{1,2})?|[0-9]+(?:\.[0-9]{1,2})?)?$/i);
    if (itemMatch) {
      const name = itemMatch[2]?.trim();
      const qty = parseFloat(itemMatch[3]) || 1;
      const unit = itemMatch[4]?.trim() || 'กล่อง';
      const price = itemMatch[5] ? parseFloat(itemMatch[5].replace(/,/g, '')) : 0;
      if (name && name.length >= 3 && !['รวม', 'total', 'grand total', 'vat', 'ภาษี'].includes(name.toLowerCase())) {
        detectedItems.push({
          item_name: name,
          quantity: qty,
          unit: unit,
          unit_price: price
        });
      }
    }
  }

  return {
    receiptNumber,
    poNumber,
    supplierName,
    receivedDate,
    totalAmount,
    detectedItems,
    rawText: text
  };
};

/**
 * Creates and triggers a download of a .txt file from string content in the browser.
 * 
 * @param {string} filename Output file name
 * @param {string} content Plain text content
 * @returns {boolean} True if download initiated
 */
export const downloadTextFile = (filename = 'receipt-ocr.txt', content = '') => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return false;
  }

  try {
    const finalFilename = filename.toLowerCase().endsWith('.txt') ? filename : `${filename}.txt`;
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = finalFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
    return true;
  } catch (err) {
    console.error('Failed to download text file:', err);
    return false;
  }
};
